import React from 'react';
import PropTypes from 'prop-types';
import BSModal from 'react-bootstrap/Modal';
import styles from './Modal.module.scss';

/* ────────────────────────────────────────────────────────────
 * Modal — centered, animated dialog with a gradient-accent rail.
 *
 *   <Modal open={open} onClose={() => setOpen(false)} title="Edit X">
 *     <ModalBody>...</ModalBody>
 *   </Modal>
 *
 * Prop unification (PROMPT_TEMPLATE.md §4 d2):
 *   Modal accepts both `open / onClose` (the unified system-wide
 *   prop pair, matches Drawer / Toast / CommandPalette) and the
 *   legacy `show / onHide` (Bootstrap-style, used by ~60+ callers
 *   shipped before unification). New code uses `open / onClose`.
 *   The legacy props will be removed in a future cleanup PR once
 *   every call site has migrated.
 *
 * For editor-modal body composition use the Modal sub-primitives
 * (ModalTitle, ModalBody, ModalSection, ModalGrid, ModalField,
 * ModalRow, ModalFooter) — they encode the editor contract once.
 * ──────────────────────────────────────────────────────────── */

const Modal = ({
  open,
  onClose,
  show,
  onHide,
  title,
  size,
  children,
  footer,
  className,
}) => {
  // Unified API wins; legacy is a fallback.
  const isOpen = open !== undefined ? open : show;
  const handleClose = onClose || onHide || (() => {});

  const classNames = [styles.modal, className || ''].filter(Boolean).join(' ');

  return (
    <div className={classNames}>
      {/* enforceFocus is disabled so focus can reach popups that portal
          outside the modal DOM (e.g. SearchableSelect / MultiSelect render
          their search input + option list into document.body — Bootstrap's
          focus trap would otherwise steal focus back and make the search
          field untypable). */}
      <BSModal
        show={isOpen}
        onHide={handleClose}
        size={size}
        centered
        enforceFocus={false}
      >
        <BSModal.Header closeButton>
          <BSModal.Title>{title}</BSModal.Title>
        </BSModal.Header>
        <BSModal.Body>{children}</BSModal.Body>
        {footer && <BSModal.Footer>{footer}</BSModal.Footer>}
      </BSModal>
    </div>
  );
};

Modal.propTypes = {
  // Unified prop pair (preferred for new code).
  open: PropTypes.bool,
  onClose: PropTypes.func,
  // Legacy prop pair (still supported; do not introduce in new code).
  show: PropTypes.bool,
  onHide: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  children: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
};

Modal.defaultProps = {
  open: undefined,
  onClose: undefined,
  show: false,
  onHide: undefined,
  title: '',
  size: 'md',
  children: null,
  footer: null,
  className: '',
};

export default Modal;
